import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { LeadCaptureForm } from '@/components/forms/LeadCaptureForm';
import { useLanguage } from '@/context/LanguageContext';
import {
  Building2, Heart, Sparkles, CheckCircle, ArrowRight, Star
} from 'lucide-react';

const corporateFeatures = [
  'On-site laser branding station',
  'Custom branded items for attendees',
  'Real-time lead capture via NFC',
  'Post-event analytics dashboard',
  'Dedicated event coordinator',
  'Premium material selection',
];

const privateFeatures = [
  'Personalized guest mementos',
  'Luxury material options',
  'Custom design consultation',
  'On-site engraving artist',
  'Premium packaging included',
  'Digital keepsake via NFC',
];

const testimonials = [
  { name: 'Sarah Klein', role: 'VP Marketing, TechCorp', text: 'The Ace transformed our product launch. Every attendee left with a personalized item and we captured 300+ leads through NFC cards.', rating: 5 },
  { name: 'David Levi', role: 'Wedding Planner', text: 'Absolutely stunning. The laser-engraved favors were the highlight of the evening. Our clients were speechless.', rating: 5 },
];

export default function EventsPage() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <div className="pt-24 pb-20">
      <section ref={ref} className="relative max-w-7xl mx-auto px-6 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl sm:text-5xl font-bold text-title mb-4">{t('events.title')}</h1>
          <p className="text-base text-body max-w-lg">{t('events.subtitle')}</p>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6">
        <Tabs defaultValue="corporate" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-secondary h-12">
            <TabsTrigger value="corporate" className="flex items-center gap-2 data-[state=active]:bg-card data-[state=active]:text-primary text-sm">
              <Building2 className="w-4 h-4" /> {t('events.corporate')}
            </TabsTrigger>
            <TabsTrigger value="private" className="flex items-center gap-2 data-[state=active]:bg-card data-[state=active]:text-accent text-sm">
              <Heart className="w-4 h-4" /> {t('events.private')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="corporate" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-2xl text-title">{t('events.corporateTitle')}</CardTitle>
                  <CardDescription className="text-body">{t('events.corporateDesc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {corporateFeatures.map((f) => (
                      <div key={f} className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-primary shrink-0" />
                        <span className="text-sm text-title">{f}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90 gap-2 text-sm shadow-silver sheen-effect">
                    Book Corporate Event <ArrowRight className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-card border-border overflow-hidden">
                <div className="relative h-full min-h-[300px]">
                  <img src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop" alt="Corporate event" className="w-full h-full object-cover opacity-70" />
                  <div className="absolute inset-0 bg-background/40" />
                  <div className="absolute bottom-6 left-6">
                    <span className="text-xs text-title bg-card/80 backdrop-blur-sm px-3 py-1 rounded-md border border-border">Corporate Excellence</span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Corporate Event Gallery */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-title mb-4">Corporate Events Gallery</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="relative rounded-lg overflow-hidden border border-border aspect-square">
                  <img src="https://images.unsplash.com/photo-1511578314322-379afb476865?w=400&h=400&fit=crop" alt="Tech conference" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="relative rounded-lg overflow-hidden border border-border aspect-square">
                  <img src="https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=400&h=400&fit=crop" alt="Product launch" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="relative rounded-lg overflow-hidden border border-border aspect-square">
                  <img src="https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=400&h=400&fit=crop" alt="Team building" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="relative rounded-lg overflow-hidden border border-border aspect-square">
                  <img src="https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=400&fit=crop" alt="Networking event" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="private" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-2xl text-title">{t('events.privateTitle')}</CardTitle>
                  <CardDescription className="text-body">{t('events.privateDesc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {privateFeatures.map((f) => (
                      <div key={f} className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-accent shrink-0" />
                        <span className="text-sm text-title">{f}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90 gap-2 text-sm shadow-purple sheen-effect">
                    Plan Private Event <ArrowRight className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-card border-border overflow-hidden">
                <div className="relative h-full min-h-[300px]">
                  <img src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&h=400&fit=crop" alt="Private event" className="w-full h-full object-cover opacity-70" />
                  <div className="absolute inset-0 bg-background/40" />
                  <div className="absolute bottom-6 left-6">
                    <span className="text-xs text-title bg-card/80 backdrop-blur-sm px-3 py-1 rounded-md border border-border">Luxury Memories</span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Private Events Gallery */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-title mb-4">Private Events Gallery</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="relative rounded-lg overflow-hidden border border-border aspect-square">
                  <img src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=400&h=400&fit=crop" alt="Wedding" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="relative rounded-lg overflow-hidden border border-border aspect-square">
                  <img src="https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400&h=400&fit=crop" alt="Birthday party" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="relative rounded-lg overflow-hidden border border-border aspect-square">
                  <img src="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&h=400&fit=crop" alt="Anniversary" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="relative rounded-lg overflow-hidden border border-border aspect-square">
                  <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&h=400&fit=crop" alt="Celebration" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <h3 className="text-2xl font-bold text-title mb-8">What Clients Say</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.map((test) => (
            <Card key={test.name} className="bg-card border-border p-6">
              <div className="flex gap-1 mb-4">
                {Array.from({ length: test.rating }).map((_, i) => (<Star key={i} className="w-4 h-4 fill-primary text-primary" />))}
              </div>
              <p className="text-sm text-title leading-relaxed mb-4">&ldquo;{test.text}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-medium text-title">{test.name[0]}</div>
                <div>
                  <p className="text-sm font-medium text-title">{test.name}</p>
                  <p className="text-xs text-body">{test.role}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto bg-border my-16" />

      {/* B2B Lead Capture Section */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-title mb-4">
            Ready to Plan Your Event?
          </h2>
          <p className="text-base text-body max-w-2xl mx-auto">
            Share your event details and we&apos;ll create a custom proposal tailored to your needs.
          </p>
        </div>
        <LeadCaptureForm 
          cardId="events-page"
          buttonText="שלח פרטי אירוע / Send Event Details"
        />
      </section>
    </div>
  );
}
